import { 
  TrendingUp, Wallet, ListChecks, Upload, History, Search, LogOut,
  PlusCircle, BarChart3, Target, Calculator, Bell, DollarSign, Scissors,
  RefreshCw, GitBranch, LineChart, Activity, Mail, Layers, ChevronDown, ChevronRight,
  Eye, CalendarClock, BookOpen, Sun, Moon, GitCompare, Filter, Newspaper,
  Scan, Calendar, Ruler, FlaskConical, Globe, Rewind, LayoutGrid, Table2, Clock, Share2,
  MessageSquare, Stethoscope, Zap, Shield, HelpCircle, Brain, Heart, Sparkles, Microscope,
  Kanban, FileBarChart, Building2, ShieldCheck, ClipboardCheck, Settings, Network, GraduationCap,
  Lock, Landmark, Gauge,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/lib/useTheme";
import { authFetch } from "@/lib/api";
import QuickAddFAB from "@/components/QuickAddFAB";
import AccountSelector from "@/components/AccountSelector";
import DisplayCurrencySelector from "@/components/DisplayCurrencySelector";
import DisplayLanguageSelector from "@/components/DisplayLanguageSelector";
import { useTranslation } from "@/hooks/useTranslation";
import { ACCESS_TIERS, canShowNavItem, isNavLocked } from "@/config/routeAccess";
import { useEntitlements } from "@/context/EntitlementsContext";
import { FEATURES } from "@/config/features";
import AchievementToastListener from "@/components/gamification/AchievementToastListener";

const BACKEND_URL = '';

export const navSections = [
  {
    label: "Core",
    items: [
      { path: "/dashboard", icon: TrendingUp, label: "Dashboard", tier: ACCESS_TIERS.FREE },
      { path: "/my-finances", icon: Wallet, label: "My Finances", tier: ACCESS_TIERS.CORE, showWhenLocked: true },
      { path: "/holdings", icon: Wallet, label: "Holdings", tier: ACCESS_TIERS.FREE },
      { path: "/discover", icon: Sparkles, label: "Discover", tier: ACCESS_TIERS.CORE, showWhenLocked: true },
      { path: "/cashflow", icon: Calculator, label: "Cashflow", tier: ACCESS_TIERS.FREE },
      { path: "/debt-optimizer", icon: Landmark, label: "Debt Optimizer", tier: ACCESS_TIERS.CORE },
      { path: "/wealth-studio", icon: LineChart, label: "Wealth Studio", tier: ACCESS_TIERS.CORE },
      { path: "/watchlist", icon: Eye, label: "Watchlist", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
      { path: "/research", icon: LineChart, label: "Research", tier: ACCESS_TIERS.FREE },
      { path: "/search", icon: Search, label: "Search", tier: ACCESS_TIERS.FREE },
    ]
  },
  {
    label: "Research",
    items: [
      { path: "/performance", icon: BarChart3, label: "Performance", tier: ACCESS_TIERS.CORE },
      { path: "/portfolio-quality", icon: ShieldCheck, label: "Stock Quality Score", tier: ACCESS_TIERS.CORE },
      { path: "/sleeves", icon: Layers, label: "Sleeves", tier: ACCESS_TIERS.CORE },
      { path: "/relationships", icon: Network, label: "Relationships", tier: ACCESS_TIERS.CORE },
      { path: "/controller", icon: Settings, label: "Controller", tier: ACCESS_TIERS.CORE },
      { path: "/holdings-intraday", icon: FileBarChart, label: "Intraday Report", tier: ACCESS_TIERS.CORE },
      { path: "/explorer", icon: Layers, label: "Explorer", tier: ACCESS_TIERS.CORE },
      { path: "/income", icon: DollarSign, label: "Income", tier: ACCESS_TIERS.CORE },
      { path: "/risk", icon: Shield, label: "Risk", tier: ACCESS_TIERS.CORE },
      { path: "/review", icon: BookOpen, label: "Review", tier: ACCESS_TIERS.CORE },
      // Owner-only: /api/council is a private API (404 for everyone else).
      { path: "/council-brief", icon: Brain, label: "Research Council", tier: ACCESS_TIERS.OWNER },
      { path: "/actions", icon: Bell, label: "Actions", tier: ACCESS_TIERS.CORE },
      { path: "/networth", icon: Layers, label: "Net Worth", tier: ACCESS_TIERS.CORE },
      { path: "/thesis", icon: BookOpen, label: "Thesis", tier: ACCESS_TIERS.CORE },
      { path: "/pipeline", icon: Kanban, label: "Pipeline", tier: ACCESS_TIERS.CORE },
      { path: "/allocation", icon: Target, label: "Allocation", tier: ACCESS_TIERS.CORE },
      { path: "/ledger", icon: BookOpen, label: "Ledger", tier: ACCESS_TIERS.CORE },
      { path: "/pl-analytics", icon: DollarSign, label: "P&L Analytics", tier: ACCESS_TIERS.CORE },
      { path: "/lens-alerts", icon: Bell, label: "Lens Alerts", tier: ACCESS_TIERS.CORE },
      { path: "/analytics", icon: BarChart3, label: "Analytics", tier: ACCESS_TIERS.CORE },
      { path: "/screener", icon: Filter, label: "Screener", tier: ACCESS_TIERS.CORE },
      { path: "/benchmark", icon: LineChart, label: "Benchmark", tier: ACCESS_TIERS.CORE },
      { path: "/market-overview", icon: Globe, label: "Market Overview", tier: ACCESS_TIERS.CORE },
      { path: "/macro", icon: Landmark, label: "Macro Context", tier: ACCESS_TIERS.CORE },
    ]
  },
  {
    label: "Learning",
    items: [
      { path: "/dictionary", icon: BookOpen, label: "Dictionary", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
      { path: "/principles", icon: GraduationCap, label: "Principles", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
      { path: "/learn/curriculum", icon: ShieldCheck, label: "BEAM Academy", tier: ACCESS_TIERS.CORE },
      { path: "/quiz", icon: HelpCircle, label: "Quiz", tier: ACCESS_TIERS.CORE },
      { path: "/brain", icon: Brain, label: "Brain Map", tier: ACCESS_TIERS.CORE },
      { path: "/tax-awareness", icon: Calculator, label: "Tax Awareness", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
      { path: "/news", icon: Newspaper, label: "News", tier: ACCESS_TIERS.CORE },
      { path: "/dictionary-review", icon: ShieldCheck, label: "Dict Review", tier: ACCESS_TIERS.CORE },
    ]
  },
  {
    label: "Tools",
    items: [
      { path: "/import", icon: Upload, label: "Import", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
      { path: "/manual-entry", icon: PlusCircle, label: "Manual Entry", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
      { path: "/account-activity", icon: Building2, label: "Accounts", tier: ACCESS_TIERS.CORE },
      { path: "/review-cadence", icon: ClipboardCheck, label: "Review", tier: ACCESS_TIERS.CORE },
      { path: "/investor-journey", icon: History, label: "Journey", tier: ACCESS_TIERS.CORE },
      { path: "/investor-score", icon: Gauge, label: "Process Score", tier: ACCESS_TIERS.CORE },
      { path: "/mutual-funds", icon: DollarSign, label: "Mutual Funds", tier: ACCESS_TIERS.CORE },
      { path: "/bdd", icon: Zap, label: "BEAM Framework", tier: ACCESS_TIERS.CORE },
      { path: "/position-sizer", icon: Ruler, label: "Position Sizer", tier: ACCESS_TIERS.CORE },
      { path: "/what-if", icon: FlaskConical, label: "What-If", tier: ACCESS_TIERS.CORE },
      { path: "/tax-loss", icon: Scissors, label: "Tax Harvest", tier: ACCESS_TIERS.CORE },
      { path: "/earnings", icon: Calendar, label: "Earnings", tier: ACCESS_TIERS.CORE },
      { path: "/peer-comparison", icon: GitCompare, label: "Peer Compare", tier: ACCESS_TIERS.CORE },
      { path: "/research-hub", icon: Globe, label: "Research Hub", tier: ACCESS_TIERS.CORE },
      { path: "/dividends", icon: DollarSign, label: "Dividends", tier: ACCESS_TIERS.CORE },
      { path: "/dca-scheduler", icon: CalendarClock, label: "DCA Scheduler", tier: ACCESS_TIERS.CORE },
      { path: "/rebalance", icon: Calculator, label: "Rebalance", tier: ACCESS_TIERS.CORE },
      { path: "/lens-diagnostics", icon: Microscope, label: "Lens Diagnostics", tier: ACCESS_TIERS.CORE },
      { path: "/portfolio-doctor", icon: Stethoscope, label: "Portfolio Doctor", tier: ACCESS_TIERS.CORE },
      { path: "/research-copilot", icon: MessageSquare, label: "Research Copilot", tier: ACCESS_TIERS.CORE },
      { path: "/strategy-labs", icon: FlaskConical, label: "Strategy Labs", tier: ACCESS_TIERS.CORE },
      { path: "/ai-engine", icon: Brain, label: "AI Engine", tier: ACCESS_TIERS.CORE },
      { path: "/access-control", icon: ShieldCheck, label: "Access Control", tier: ACCESS_TIERS.FREE, showInFreeNav: false },
    ]
  },
  {
    label: "Private",
    ownerOnly: true,
    items: [
      { path: "/neo-intelligence", icon: Brain, label: "NEO Intelligence", tier: ACCESS_TIERS.OWNER },
      { path: "/fund-os", icon: Building2, label: "Fund OS", tier: ACCESS_TIERS.OWNER },
    ]
  },
];

const mobileNavItems = [
  { path: '/dashboard', icon: TrendingUp, label: 'Home', tier: ACCESS_TIERS.FREE },
  { path: '/my-finances', icon: Wallet, label: 'Finances', tier: ACCESS_TIERS.CORE, showWhenLocked: true },
  { path: '/holdings', icon: LineChart, label: 'Portfolio', tier: ACCESS_TIERS.FREE },
  { path: '/discover', icon: Sparkles, label: 'Discover', tier: ACCESS_TIERS.CORE, showWhenLocked: true },
  { path: '/cashflow', icon: Calculator, label: 'Cashflow', tier: ACCESS_TIERS.FREE },
];

const sectionTranslationKeys = {
  Core: "nav_core",
  Research: "nav_analytics",
  Learning: "nav_learning",
  Tools: "nav_tools",
  More: "nav_more",
  "Coming Soon": "nav_coming_soon",
  Private: "nav_private",
};

const navTranslationKeys = {
  Dashboard: "nav_dashboard",
  Home: "nav_home",
  Finances: "nav_finances",
  "My Finances": "nav_my_finances",
  Holdings: "nav_holdings",
  Portfolio: "nav_portfolio",
  Discover: "nav_discover",
  Actions: "nav_actions",
  Search: "nav_search",
  Review: "nav_review",
  "Research Council": "nav_research_council",
  Cashflow: "nav_cashflow",
  "Debt Optimizer": "nav_debt_optimizer",
  "BEAM Academy": "nav_beam_academy",
  Risk: "nav_risk",
  "Stock Quality Score": "nav_stock_quality_score",
  "Portfolio Quality": "nav_stock_quality_score",
  Watchlist: "nav_watchlist",
  Thesis: "nav_thesis",
  Income: "nav_income",
  "Net Worth": "nav_net_worth",
  Relationships: "nav_relationships",
  Allocation: "nav_allocation",
  Import: "nav_import",
  Accounts: "nav_accounts",
};

function translatedLabel(t, label, map) {
  const key = map[label];
  return key ? t(key) : label;
}

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const entitlements = useEntitlements();
  const { theme, toggleTheme } = useTheme();
  const [expandedSections, setExpandedSections] = useState(() => {
    const active = navSections.find(s => s.items.some(i => i.path === location.pathname));
    const initial = { [active?.label || "Core"]: true };
    // "More" always starts collapsed unless user is on a "More" route
    if (active?.label !== "More") initial["More"] = false;
    return initial;
  });
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [backendHealthy, setBackendHealthy] = useState(true);
  const [checkingBackend, setCheckingBackend] = useState(false);
  const [isWakingUp, setIsWakingUp] = useState(false);
  const [alertBadge, setAlertBadge] = useState(0);
  const mainRef = useRef(null);
  const visibleNavSections = useMemo(() => {
    return navSections
      .filter((section) => !section.ownerOnly || entitlements.is_owner)
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => canShowNavItem(item, entitlements)),
      }))
      .filter((section) => section.items.length > 0);
  }, [entitlements]);

  useEffect(() => {
    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const checkBackend = useCallback(async () => {
    if (!navigator.onLine) {
      setBackendHealthy(false);
      setIsWakingUp(false);
      return;
    }
    setCheckingBackend(true);
    // Show "waking up" hint after 3s of no response (Railway cold start)
    const wakeTimer = setTimeout(() => setIsWakingUp(true), 3000);
    try {
      const res = await authFetch(`/api/ping`);
      setBackendHealthy(res.ok);
      setIsWakingUp(false);
    } catch {
      setBackendHealthy(false);
    } finally {
      clearTimeout(wakeTimer);
      setCheckingBackend(false);
    }
  }, []);

  useEffect(() => {
    checkBackend();
    const interval = setInterval(checkBackend, 45000);
    return () => clearInterval(interval);
  }, [checkBackend, location.pathname]);

  // Fetch unread lens alert count
  useEffect(() => {
    const fetchBadge = async () => {
      try {
        const res = await authFetch(`/api/lens-alerts/notifications?limit=1`);
        if (res.ok) {
          const data = await res.json();
          setAlertBadge(data.unread_count || 0);
        }
      } catch {}
    };
    fetchBadge();
    const interval = setInterval(fetchBadge, 120000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Scroll to top on route change
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await authFetch(`${BACKEND_URL}/api/auth/logout`, { method: 'POST' });
    } catch (error) {
      console.error('Logout failed:', error);
    }
    // Session cleared via HttpOnly cookie on /api/auth/logout
    navigate('/login');
  };

  const toggleSection = (label) => {
    setExpandedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Offline Banner */}
      {!isOnline && (
        <div data-testid="offline-banner" className="fixed top-0 left-0 right-0 z-[60] bg-amber-500/90 text-black text-center text-xs font-medium py-1">
          You're offline. Live prices and backend actions are temporarily unavailable.
        </div>
      )}
      {isOnline && (isWakingUp || !backendHealthy) && (
        <div
          data-testid="backend-banner"
          className={`fixed top-0 left-0 right-0 z-[60] text-center text-xs font-medium py-1 px-3 ${
            isWakingUp
              ? 'bg-amber-400/90 text-black'
              : 'bg-rose-500/90 text-white'
          }`}
        >
          <div className="flex items-center justify-center gap-3">
            {isWakingUp ? (
              <span>Waking up server&hellip; hang tight, this takes a moment.</span>
            ) : (
              <span>Backend is unreachable right now. Market data and protected actions may be stale or unavailable.</span>
            )}
            {!isWakingUp && (
              <button onClick={checkBackend} className="underline underline-offset-2">
                {checkingBackend ? "Retrying..." : "Retry"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <header className={`sticky ${(!isOnline || (isOnline && !backendHealthy)) ? 'top-6' : 'top-0'} z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60`}>
        <div className="container flex h-14 items-center px-4">
          <div className="flex items-center gap-2">
            <div className="md:hidden">
              <AccountSelector triggerVariant="icon" />
            </div>
            <div className="flex items-center gap-2 font-heading font-bold text-lg cursor-pointer select-none" onClick={() => navigate('/dashboard')}>
              <TrendingUp className="h-5 w-5 text-primary" />
              <span className="hidden sm:inline">Investing OS</span>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1">
            <div className="hidden sm:flex items-center gap-1">
              <DisplayLanguageSelector compact />
              <DisplayCurrencySelector compact />
            </div>
            {FEATURES.neoIntelligence && (
              <Button
                data-testid="neo-brain-shortcut"
                variant={location.pathname === "/neo-intelligence" ? "secondary" : "ghost"}
                size="sm"
                onClick={() => navigate('/neo-intelligence')}
                className="gap-2"
                title="Open NEO Brain"
              >
                <Brain className="h-4 w-4" />
                <span className="hidden sm:inline">NEO</span>
              </Button>
            )}
            <Button
              data-testid="theme-toggle"
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              className="gap-2"
              title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            >
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={handleLogout} className="gap-2">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("nav_logout")}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop Sidebar Nav */}
      <nav className="hidden md:flex fixed left-0 top-14 bottom-0 w-56 border-r border-border bg-card flex-col overflow-y-auto">
        <div className="flex flex-col gap-0.5 p-3 flex-1">
          {/* Account Selector (shown when multiple accounts exist) */}
          <div className="mb-1">
            <AccountSelector />
          </div>
          {visibleNavSections.map((section) => {
            const isExpanded = expandedSections[section.label];
            return (
              <div key={section.label}>
                <button
                  onClick={() => toggleSection(section.label)}
                  className="flex items-center justify-between w-full px-3 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                >
                  {translatedLabel(t, section.label, sectionTranslationKeys)}
                  {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
                {isExpanded && section.items.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  const badge = item.path === '/lens-alerts' ? alertBadge : 0;
                  const locked = isNavLocked(item, entitlements);
                  const comingSoon = item.tier === ACCESS_TIERS.COMING_SOON;
                  return (
                    <Button
                      key={item.path}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      variant={isActive ? "secondary" : "ghost"}
                      size="sm"
                      className="justify-start gap-2 w-full text-sm h-9"
                      onClick={() => navigate(item.path)}
                      title={locked ? "Unlock ROOH Core" : undefined}
                    >
                      <Icon className="h-4 w-4" />
                      {translatedLabel(t, item.label, navTranslationKeys)}
                      {locked && <Lock className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
                      {comingSoon && (
                        <span className="ml-auto rounded-full border border-border px-1.5 py-0.5 text-[9px] uppercase text-muted-foreground">
                          Soon
                        </span>
                      )}
                      {badge > 0 && (
                        <span data-testid="lens-alerts-badge" className="ml-auto flex h-5 min-w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground px-1">
                          {badge > 99 ? '99+' : badge}
                        </span>
                      )}
                    </Button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </nav>

      {/* Main Content with page transition */}
      <main
        ref={mainRef}
        className="w-full md:w-[calc(100%-14rem)] md:ml-56 container max-w-7xl mx-auto px-4 py-6 pb-28 md:pb-6 animate-in fade-in duration-200"
        style={{ paddingBottom: "calc(7rem + env(safe-area-inset-bottom, 0px))" }}
        key={location.pathname}
      >
        {children}
      </main>

      <AchievementToastListener />

      {/* Quick Add FAB (mobile only) */}
      <QuickAddFAB />

      {/* Mobile Bottom Nav — enhanced with safe area */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-lg border-t border-border md:hidden">
        <div className="grid grid-cols-5 gap-0.5 px-1 pt-1" style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px))" }}>
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            const locked = isNavLocked(item, entitlements);
            return (
              <button
                key={item.path}
                data-testid={`nav-mobile-${item.label.toLowerCase()}`}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 min-h-[48px] py-2.5 px-1 rounded-xl transition-all duration-150 active:scale-95 ${
                  isActive
                    ? 'text-primary bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className={`h-5 w-5 transition-transform duration-150 ${isActive ? 'scale-110' : ''}`} />
                <span className="flex items-center gap-0.5 text-[10px] font-medium leading-none">
                  {translatedLabel(t, item.label, navTranslationKeys)}
                  {locked && <Lock className="h-2.5 w-2.5" />}
                </span>
                {isActive && <div className="w-1 h-1 rounded-full bg-primary mt-0.5" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
